import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Price from 'ui/price'
import { client } from 'utils/api-client'
import { ShoppingCart, Heart } from 'lucide-react'
import { Toast } from './Toast'
import { useWishlist } from 'context/wishlist-context'
import { ProductImage } from 'utils/imageUtils'

interface Attributes {
  appearance: string
  author: string
  pagesNumber: string
  publishYear: string
  translator: string
}

interface Comment {
  id: number
  customerFirstName: string
  customerLastName: string
  customerId: string
  rating: number
  comment: string
  createdAt: string
}

interface Book {
  attributes: Attributes
  price: number
  title: string
  vendor: string
  barcode: string
  compareAtPrice: number | null
  createdAt: Date
  description: string
  id: string
  images: ProductImage[]
  orderId: string | null
  ownerCellphone: string
  ownerId: string
  ownerLastName: string
  priceCurrency: string
  format?: string
  subject?: string
  cover?: string
  categoryTitle?: string
  reviews?: Comment[]
  averageRating?: number
  approvedReviewCount?: number
  reviewCount?: number
  comments?: Comment[]
}

export const PurchasePanel = ({ book }: { book: Book }) => {
  const [cartId, setCartId] = useState(() => {
    const items = localStorage.getItem('cart')
    const initialValue = items ? JSON.parse(items) : ''
    return initialValue.id || ''
  })
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const { isInWishlist, toggleItem, loading: wishlistLoading } = useWishlist()

  //need to be implement when add product to cart
  const toastMessageText = `"${book.title}" به سبد خرید اضافه شد!`
  const inWishlist = isInWishlist(book.id)

  const navigate = useNavigate()

  const addItemToCart = async () => {
    let data = {
      itemId: book.id,
      itemType: 'product',
      quantity: 1,
    }

    if (!cartId) {
      const result = await client('orders', { data })
      setCartId(result.id)

      if (result) {
        localStorage.setItem('cart', JSON.stringify(result))
        window.dispatchEvent(new Event('storage'))
        setToastMessage(toastMessageText)
        navigate('/cart')
      }
    }

    if (cartId) {
      const result = await client(`orders/${cartId}/items`, { data })
      if (result) {
        localStorage.setItem('cart', JSON.stringify(result))
        window.dispatchEvent(new Event('storage'))
        setToastMessage(toastMessageText)
        navigate('/cart')
      }
    }
  }

  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (wishlistLoading) return

    const success = await toggleItem(book.id)
    if (success) {
      // Optional: Show toast notification
      console.log(
        inWishlist
          ? 'محصول از علاقه‌مندی‌ها حذف شد'
          : 'محصول به علاقه‌مندی‌ها اضافه شد',
      )
    }
  }

  let appearance
  if (book?.attributes.appearance === 'new') {
    appearance = 'نو'
  } else if (book?.attributes.appearance === 'like-new') {
    appearance = 'در حد نو'
  } else if (book?.attributes.appearance === 'good') {
    appearance = 'خوب'
  } else {
    appearance = 'قابل قبول'
  }

  return (
    <div
      className={`
        w-full
        px-4 py-4
        rounded-t-lg shadow-t
        fixed bottom-0 left-0 z-50
        flex flex-row justify-between items-center
        lg:static lg:rounded-xl lg:shadow-sm lg:px-6 lg:py-10 lg:w-96 lg:top-8 lg:flex-col-reverse lg:items-stretch
      `}
      style={{ backgroundColor: 'var(--card-bg)' }}
    >
      {toastMessage && (
        <Toast message={toastMessage} onDismiss={() => setToastMessage(null)} />
      )}
      <div className='flex flex-col items-end flex-1 max-w-[200px] md:max-w-[300px]'>
        <button
          onClick={addItemToCart}
          className='
            w-full flex items-center justify-center text-white font-bold py-4 px-3 rounded-lg text-base
            transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            md:mb-2 
            lg:mt-10 md:text-lg md:py-3 md:px-4
          '
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          <ShoppingCart size={22} />
          <span className='mr-2'>افزودن به سبد خرید</span>
        </button>
        <button
          onClick={handleWishlistToggle}
          disabled={wishlistLoading}
          className='
            w-full flex items-center justify-center text-white font-bold py-3 px-3 rounded-lg text-base
            transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2
            mt-2
            md:text-base md:py-2 md:px-3
            disabled:opacity-50 disabled:cursor-not-allowed
          '
          style={{ backgroundColor: 'var(--accent-secondary, #64748b)' }}
        >
          <Heart
            size={20}
            fill={inWishlist ? 'currentColor' : 'none'}
            className={`ml-2 transition-colors duration-200 ${
              inWishlist ? 'text-red-300' : 'text-white'
            }`}
            strokeWidth={2}
          />
          <span>
            {inWishlist ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}
          </span>
        </button>
        <p
          className='block lg:hidden text-sm font-semibold -ml-2 mt-1 sm:text-base md:text-lg md:mt-1 text-right w-full'
          style={{ color: 'var(--accent-primary)' }}
        >
          موجود در انبار
        </p>
      </div>
      <div className='flex flex-col flex-1 items-end lg:items-start'>
        <div className='w-full text-lg sm:text-xl md:text-3xl md:my-2 text-left lg:text-right font-bold'>
          {book?.compareAtPrice ? (
            <>
              <span
                className='ml-4'
                style={{
                  color: 'var(--text-secondary)',
                  textDecoration: 'line-through',
                  textDecorationThickness: '2px',
                  textDecorationColor: 'var(--text-secondary)',
                  fontSize: '23px',
                }}
              >
                <Price
                  value={book?.price}
                  currencyCode={book?.priceCurrency}
                  alternativeCurrencyCode={true}
                />
              </span>
              <span style={{ color: 'var(--text-primary)' }}>
                <Price
                  value={book?.compareAtPrice}
                  currencyCode={book?.priceCurrency}
                  alternativeCurrencyCode={true}
                />
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--text-primary)' }}>
              <Price
                value={book?.price}
                currencyCode={book?.priceCurrency}
                alternativeCurrencyCode={true}
              />
            </span>
          )}
        </div>
        <p
          className='hidden lg:block font-semibold text-lg text-right w-full'
          style={{ color: 'var(--accent-primary)' }}
        >
          موجود در انبار
        </p>
        <h4
          className='hidden md:block text-sm sm:text-base font-medium mb-2 md:text-lg md:mb-0 lg:mt-2 lg:text-right'
          style={{ color: 'var(--text-secondary)' }}
        >
          وضعیت ظاهری کتاب <span className='font-semibold'>{appearance}</span>{' '}
          است.
        </h4>
        <h4
          className='block md:hidden text-sm sm:text-base font-medium md:mb-2 md:text-lg'
          style={{ color: 'var(--text-secondary)' }}
        >
          وضعیت ظاهری <span className='font-semibold'>{appearance}</span>{' '}
        </h4>
      </div>
    </div>
  )
}
